import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box/box.component';
import Textbox from 'carbon-react/lib/components/textbox';

import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { adaptSelectChange } from '../../../utils/carbon-select';
import { AddToGroupMenu } from '../add-to-group-menu/add-to-group-menu';
import { DecimalPlacesMenu } from '../decimal-places-menu/decimal-places-menu';
import { InputMethod } from '../../../ui/data-builder/types';
import { InputMethodMenu } from '../../../ui/data-builder/menus/input-method-menu/input-method-menu';
import { NonFinancialDriverTreeNode } from '../../types';

import styles from './driver-control-bar.scss';

interface DriverControlBarProps {
  addToGroup?: string;
  decimalDisplay?: string;
  groups?: NonFinancialDriverTreeNode[];
  errors?: WizardFormErrors;
  inputMethod?: InputMethod;
  isAddingNew?: boolean;
  isDisabled?: boolean;
  name?: string;
  onAddToGroupChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDecimalDisplayChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFieldBlur?: () => void;
  onInputMethodChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onNameChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const availableInputMethods = [InputMethod.FORMULA, InputMethod.FREESTYLE, InputMethod.EXCEL];

export const DriverControlBar = ({
  addToGroup,
  decimalDisplay,
  errors,
  groups,
  inputMethod,
  isAddingNew,
  isDisabled,
  name,
  onAddToGroupChange,
  onDecimalDisplayChange,
  onFieldBlur,
  onInputMethodChange,
  onNameChange
}: DriverControlBarProps) => {
  const intl = useIntl();

  return (
    <>
      <Box mb={3}>
        <WizardRequiredFieldInfo />
      </Box>
      <div className={styles.menuBarCarbon} data-testid="menuBar">
        <Box width={240}>
          <InputMethodMenu
            options={availableInputMethods}
            className={clsx(styles.formInput, styles.small)}
            disabled={isDisabled || !isAddingNew}
            label={intl.formatMessage({
              id: 'predict.non_financial_drivers.creation_method',
              defaultMessage: 'Creation method'
            })}
            onChange={onInputMethodChange}
            value={inputMethod}
            size="medium"
            required
          />
        </Box>
        {inputMethod !== InputMethod.EXCEL && (
          <>
            <Box width={280}>
              <Textbox
                id="name"
                label={intl.formatMessage({
                  id: 'predict.non_financial_drivers.driver_name',
                  defaultMessage: 'Driver name'
                })}
                placeholder={intl.formatMessage({
                  id: 'common.enter_name',
                  defaultMessage: 'Enter name'
                })}
                value={name}
                onBlur={onFieldBlur}
                onChange={onNameChange ?? (() => undefined)}
                disabled={isDisabled}
                error={errors?.name?.message}
                size="medium"
                mb={0}
                required
              />
            </Box>
            <Box width={240} ml={16}>
              <AddToGroupMenu
                groups={groups}
                value={addToGroup ?? ''}
                onChange={adaptSelectChange(onAddToGroupChange)}
                mb={0}
                size="medium"
              />
            </Box>
            <Box width={160} ml={16}>
              <DecimalPlacesMenu
                value={decimalDisplay ?? ''}
                onChange={adaptSelectChange(onDecimalDisplayChange)}
                size="medium"
              />
            </Box>
          </>
        )}
      </div>
    </>
  );
};
